import { Navigate, Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard, Building2, Ticket, LogOut, Shield, Users, UserCog, ShieldCheck, Settings, Monitor,
  PanelLeftClose, PanelLeftOpen, Menu, X, Bell, HelpCircle, User, ChevronDown, Search, Rocket, Book,
  Sun, Moon, Mail, RefreshCw
} from 'lucide-react';

const SuperAdminLayout = () => {
  const { admin, loading, logout } = useSuperAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('equipr_sa_sidebar_collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('equipr_sa_theme');
    return saved || 'dark';
  });

  useEffect(() => {
    localStorage.setItem('equipr_sa_sidebar_collapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem('equipr_sa_theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  // Theme-aware classes
  const isDark = theme === 'dark';
  const themeClasses = {
    // Main backgrounds
    pageBg: isDark ? 'bg-slate-950' : 'bg-gray-100',
    headerBg: isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200 shadow-sm',
    sidebarBg: isDark ? 'bg-slate-900' : 'bg-white border-r border-gray-200',
    // Text colors
    textPrimary: isDark ? 'text-white' : 'text-gray-900',
    textSecondary: isDark ? 'text-slate-400' : 'text-gray-500',
    textMuted: isDark ? 'text-slate-500' : 'text-gray-400',
    // Interactive elements
    navItem: isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
    navItemActive: isDark ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-900',
    buttonGhost: isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100',
    // Inputs
    searchBg: isDark ? 'bg-slate-800 text-slate-500' : 'bg-gray-100 text-gray-500',
    // Borders
    borderColor: isDark ? 'border-slate-800' : 'border-gray-200',
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${themeClasses.pageBg} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  if (!admin) {
    return <Navigate to="/super-admin/login" replace />;
  }

  const navItems = [
    { path: '/super-admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/super-admin/tenants', icon: Building2, label: 'Tenants' },
    { path: '/super-admin/demo-tenants', icon: Building2, label: 'Demo Tenants', adminOnly: true },
    { path: '/super-admin/tickets', icon: Ticket, label: 'Support Tickets' },
    { path: '/super-admin/sessions', icon: Monitor, label: 'Active Sessions' },
    { path: '/super-admin/auth-migration', icon: RefreshCw, label: 'Auth Migration', adminOnly: true },
    { path: '/super-admin/emails', icon: Mail, label: 'Email Testing', adminOnly: true },
    { path: '/super-admin/roadmap', icon: Rocket, label: 'Roadmap', adminOnly: true },
    { path: '/super-admin/help', icon: Book, label: 'Help Center', adminOnly: true },
    { path: '/super-admin/agents', icon: UserCog, label: 'Support Agents', adminOnly: true },
    { path: '/super-admin/security', icon: ShieldCheck, label: 'Security (2FA)' },
    { path: '/super-admin/settings', icon: Settings, label: 'System Settings', adminOnly: true },
  ];

  const filteredNavItems = navItems.filter(item => !item.adminOnly || admin.role === 'admin');

  const NavItem = ({ path, icon: Icon, label }) => {
    const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
    
    if (sidebarCollapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Link
              to={path}
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${
                isActive
                  ? themeClasses.navItemActive
                  : themeClasses.navItem
              }`}
            >
              <Icon className="h-5 w-5" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10} className="font-medium">
            {label}
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <Link
        to={path}
        onClick={() => setMobileMenuOpen(false)}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
          isActive
            ? themeClasses.navItemActive
            : themeClasses.navItem
        }`}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span className="truncate">{label}</span>
      </Link>
    );
  };

  return (
    <TooltipProvider>
      <div className={`min-h-screen ${themeClasses.pageBg} flex flex-col ${isDark ? 'dark' : ''}`}>
        {/* Top Navigation Bar */}
        <header className={`fixed top-0 left-0 right-0 h-12 ${themeClasses.headerBg} border-b flex items-center justify-between px-4 z-50`}>
          {/* Left side - Logo & Mobile menu */}
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden p-2 rounded-md ${themeClasses.buttonGhost}`}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded overflow-hidden">
                <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
              </div>
              <div className="hidden sm:block">
                <span className={`font-heading font-bold text-sm ${themeClasses.textPrimary}`}>EQUIPR CORE</span>
                <span className={`text-[10px] ${themeClasses.textMuted} ml-1.5 uppercase tracking-wider`}>Super Admin</span>
              </div>
            </div>
          </div>

          {/* Center - Search placeholder */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <div className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm ${themeClasses.searchBg}`}>
              <Search className="h-4 w-4" />
              <span>Search tenants, tickets...</span>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-1">
            {/* Theme Toggle */}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className={`h-8 w-8 ${themeClasses.buttonGhost}`}
                  onClick={toggleTheme}
                  data-testid="theme-toggle-btn"
                >
                  {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isDark ? 'Light Mode' : 'Dark Mode'}</TooltipContent>
            </Tooltip>

            {/* Help */}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className={`h-8 w-8 ${themeClasses.buttonGhost}`}>
                  <HelpCircle className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Help</TooltipContent>
            </Tooltip>

            {/* Notifications */}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className={`h-8 w-8 ${themeClasses.buttonGhost}`}>
                  <Bell className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Notifications</TooltipContent>
            </Tooltip>

            {/* Settings (Admin only) */}
            {admin.role === 'admin' && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className={`h-8 w-8 ${themeClasses.buttonGhost}`}
                    onClick={() => navigate('/super-admin/settings')}
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>System Settings</TooltipContent>
              </Tooltip>
            )}

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className={`h-8 gap-2 px-2 ${isDark ? 'text-slate-300 hover:text-white hover:bg-slate-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}>
                  <div className="w-6 h-6 rounded-full bg-[#ffd108] flex items-center justify-center">
                    <User className="h-3.5 w-3.5 text-black" />
                  </div>
                  <span className={`hidden md:block text-xs font-medium max-w-[100px] truncate`}>
                    {admin.full_name}
                  </span>
                  <ChevronDown className="h-3 w-3 hidden md:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{admin.full_name}</p>
                  <p className="text-xs text-muted-foreground">{admin.username} • {admin.role}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/super-admin/security')}>
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  Security Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Layout */}
        <div className="flex pt-12 min-h-screen">
          {/* Sidebar */}
          <aside
            className={`fixed top-12 left-0 h-[calc(100vh-3rem)] ${themeClasses.sidebarBg} transition-all duration-200 z-40 flex flex-col ${
              sidebarCollapsed ? 'w-14' : 'w-56'
            } ${
              mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            } md:translate-x-0`}
          >
            {/* Navigation */}
            <ScrollArea className="flex-1 py-3">
              <nav className={`space-y-1 ${sidebarCollapsed ? 'px-2' : 'px-2'}`}>
                {filteredNavItems.map((item) => (
                  <NavItem key={item.path} {...item} />
                ))}
              </nav>
            </ScrollArea>

            {/* Collapse Toggle */}
            <div className={`border-t ${themeClasses.borderColor} p-2 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    data-testid="sidebar-collapse-btn"
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${themeClasses.navItem} transition-colors ${
                      sidebarCollapsed ? 'justify-center w-10 h-10' : 'w-full'
                    }`}
                  >
                    {sidebarCollapsed ? (
                      <PanelLeftOpen className="h-4 w-4" />
                    ) : (
                      <>
                        <PanelLeftClose className="h-4 w-4" />
                        <span className="hidden md:inline">Collapse</span>
                      </>
                    )}
                  </button>
                </TooltipTrigger>
                {sidebarCollapsed && (
                  <TooltipContent side="right">Expand sidebar</TooltipContent>
                )}
              </Tooltip>
            </div>
          </aside>

          {/* Overlay for mobile */}
          {mobileMenuOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-30 md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}

          {/* Main Content */}
          <main className={`flex-1 transition-all duration-200 ${
            sidebarCollapsed ? 'md:ml-14' : 'md:ml-56'
          }`}>
            <div className="p-4 md:p-6">
              <Outlet context={{ theme, isDark }} />
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default SuperAdminLayout;
